import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { getProductsSortedByAddedAtDesc } from "./utils/productSorter";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db, auth } from "./firebase";

export default function SharedWishlistPage() {
  const { id } = useParams();
  const { t } = useTranslation();
  const userId = auth.currentUser?.uid || null;
  const [wishlist, setWishlist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchWishlist() {
      try {
        const ref = doc(db, "wishlists", id);
        const snap = await getDoc(ref);

        if (snap.exists()) {
          const data = snap.data();
          console.log("📋 Dati wishlist:", data);
          console.log("🔗 sharedLink campo:", data.sharedLink);
          
          // Le wishlist condivise devono avere sharedLink = true
          // Se non è impostato, proviamo a leggerlo comunque (per le wishlist create prima)
          const isSharedLink = data.sharedLink === true;
          const isOwner = userId && data.userId === userId;
          
          console.log("🔍 isSharedLink:", isSharedLink);
          console.log("👤 isOwner:", isOwner);
          
          // Se non è condivisa e non è il proprietario, nega l'accesso
          if (!isSharedLink && !isOwner) {
            console.warn("❌ Accesso negato: wishlist non condivisa pubblicamente.");
            setWishlist(null);
            return;
          }

          // Se è il proprietario ma la wishlist non ha sharedLink impostato, lo impostiamo
          if (isOwner && data.sharedLink === undefined) {
            console.log("🔧 Impostando sharedLink a true per la wishlist condivisa");
            await updateDoc(ref, { sharedLink: true });
            data.sharedLink = true;
          }

          const wishlistData = { id: snap.id, ...data };
          console.log("✅ Wishlist caricata con successo:", wishlistData);
          setWishlist(wishlistData);
        } else {
          console.error("❌ Wishlist non trovata");
          setWishlist(null);
        }
      } catch (err) {
        console.error("Errore nel caricamento della wishlist:", err);
        setWishlist(null);
      } finally {
        setLoading(false);
      }
    }

    fetchWishlist();
  }, [id, userId]);

  const toggleReservation = async (productIndex) => {
    if (!userId || !wishlist) return;

    const updatedProducts = [...wishlist.products];
    const product = updatedProducts[productIndex];
    const alreadyReserved = product.reservedBy === userId;

    product.reservedBy = alreadyReserved ? null : userId;

    const updatedWishlist = { ...wishlist, products: updatedProducts };
    setWishlist(updatedWishlist);

    try {
      await updateDoc(doc(db, "wishlists", wishlist.id), {
        products: updatedProducts,
      });
    } catch (error) {
      console.error("Errore aggiornamento prenotazione:", error);
      setWishlist(wishlist);
    }
  };

  if (loading) {
    return <div style={styles.message}>{t('common.loading')}</div>;
  }

  if (!wishlist || !wishlist.name) {
    return (
      <div style={styles.page}>
        <div style={styles.adHeader}>
          <h1 style={styles.brand}>Wishare</h1>
          <p style={styles.subtitle}>{t('app.subtitle')}</p>
        </div>
        <div style={styles.message}>{t('errors.wishlistNotFound') || "Wishlist non disponibile o non condivisa."}</div>
        <div style={styles.loginPrompt} onClick={() => navigate("/login")}>
          {t('auth.loginToReserve') || "Accedi per visualizzare altre funzionalità"}
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.adHeader}>
        <h1 style={styles.brand}>Wishare</h1>
        <p style={styles.subtitle}>{t('app.subtitle')}</p>
      </div>

      <h2 style={styles.title}>{wishlist.name}</h2>

      {(wishlist.tags || []).length > 0 && (
        <div style={styles.tagContainer}>
          {wishlist.tags.map((tag, i) => (
            <span key={i} style={styles.tag}>#{tag}</span>
          ))}
        </div>
      )}

      <div style={styles.grid}>
        {(wishlist.products || []).map((p, i) => (
          <a
            key={i}
            href={p.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              ...styles.card,
              ...(hoveredIndex === i ? styles.cardHover : {})
            }}
            onMouseEnter={() => setHoveredIndex(i)}
            onMouseLeave={() => setHoveredIndex(null)}
          >
            <img src={p.image} alt={p.title} style={styles.imageLarge} />
            <div style={styles.cardContent}>
              <div style={styles.link}>{p.title}</div>
              {userId ? (
                <button
                  style={{
                    ...styles.reserveBtn,
                    background: p.reservedBy
                      ? (p.reservedBy === userId ? "#999" : "#ccc")
                      : "#000",
                    cursor: p.reservedBy && p.reservedBy !== userId ? "not-allowed" : "pointer"
                  }}
                  disabled={p.reservedBy && p.reservedBy !== userId}
                  onClick={(e) => {
                    e.preventDefault();
                    toggleReservation(i);
                  }}
                >
                  {p.reservedBy
                    ? (p.reservedBy === userId ? t('products.reserved') : t('products.alreadyReserved'))
                    : t('products.reserve')}
                </button>
              ) : (
                <div
                  style={styles.loginPrompt}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate("/login");
                  }}
                >
                  {t('auth.loginToReserve') || "Accedi per prenotare"}
                </div>
              )}
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}

const styles = {
  page: {
    padding: "20px",
    fontFamily: "Poppins, sans-serif",
    background: "#fff",
    minHeight: "100vh",
    color: "#000",
    textAlign: "center"
  },
  message: {
    padding: "20px",
    fontFamily: "Poppins, sans-serif"
  },
  adHeader: {
    marginBottom: "10px"
  },
  brand: {
    fontSize: "22px",
    fontWeight: "700",
    margin: 0
  },
  subtitle: {
    fontSize: "13px",
    marginTop: "4px",
    color: "#666"
  },
  title: {
    fontSize: "20px",
    fontWeight: "600",
    marginBottom: "20px"
  },
  tagContainer: {
    display: "flex",
    gap: "8px",
    flexWrap: "wrap",
    justifyContent: "center",
    marginBottom: "20px"
  },
  tag: {
    fontSize: "12px",
    background: "#eee",
    padding: "4px 8px",
    borderRadius: "20px",
    color: "#333"
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: "20px",
    justifyItems: "center"
  },
  card: {
    background: "#f9f9f9",
    border: "1px solid #ddd",
    borderRadius: "10px",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    transition: "transform 0.25s ease, box-shadow 0.25s ease",
    boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
    textDecoration: "none",
    color: "#000",
    maxWidth: "300px"
  },
  cardHover: {
    transform: "scale(1.02)",
    boxShadow: "0 4px 12px rgba(0,0,0,0.12)"
  },
  imageLarge: {
    width: "100%",
    height: "auto",
    aspectRatio: "1 / 1",
    objectFit: "cover",
    transition: "transform 0.3s ease"
  },
  cardContent: {
    padding: "12px"
  },
  link: {
    fontWeight: "500",
    fontSize: "16px",
    marginBottom: "10px"
  },
  reserveBtn: {
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    padding: "8px 12px",
    fontSize: "14px",
    cursor: "pointer"
  },
  loginPrompt: {
    fontSize: "14px",
    color: "#000",
    textDecoration: "underline",
    cursor: "pointer",
    fontWeight: "500"
  }
};